import * as React from "react";
import { Autocomplete, TextField, FormControl, Box, styled, Chip, IconButton, Checkbox, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { useFilterStyles } from "../../styles/filterStyles";
import VirtualizedListbox from "./VirtualizedListbox";

const StyledChip = styled(Chip)({
  height: '28px',
  fontSize: '12px',
  margin: '2px',
  backgroundColor: '#D1FAE5',
  color: '#065F46',
  borderRadius: '16px',
  border: '1px solid #A7F3D0',
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: '#065F46',
    '&:hover': {
      color: '#047857',
    },
  },
});

const StyledCheckbox = styled(Checkbox)({
  padding: '4px',
  color: '#D1D5DB',
  borderRadius: '4px',
  '&.Mui-checked': {
    color: '#3B82F6',
  },
});

interface KeywordFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
  options?: string[];
  onActivate?: () => void;
  onDeactivate?: () => void;
  level?: number;
  onClearLevel?: (level: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

export const KeywordFilter: React.FC<KeywordFilterProps> = ({
  value,
  onChange,
  options,
  onActivate,
  onDeactivate,
  level,
  onClearLevel,
  isLoading,
  disabled,
}) => {
  const filterStyles = useFilterStyles();

  const keywordOptions = options ?? [];
  const normalizedValue = Array.isArray(value) ? value : [];
  const [searchText, setSearchText] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const autocompleteRef = React.useRef<HTMLDivElement>(null);
  const justOpenedRef = React.useRef(false);

  const handleChipDelete = (keywordToDelete: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onChange(normalizedValue.filter((item) => item !== keywordToDelete));
  };

  const handleResetSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onChange([]);
  };

  const handleSearchBlur = React.useCallback((e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (
      relatedTarget?.closest('.MuiAutocomplete-popper') ||
      (relatedTarget && autocompleteRef.current?.contains(relatedTarget))
    ) {
      return;
    }
    setSearchText('');
    setOpen(false);
    onDeactivate?.();
  }, [onDeactivate]);

  const handleClearLevel = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (level && onClearLevel) {
      onClearLevel(level);
    }
  };

  return (
    <>
      {/* Header with Label and Reset Selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', ...(disabled && { opacity: 0.5 }) }}>
        <label className={filterStyles.labelText}>Keywords{level ? ` (${level})` : ""}</label>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {normalizedValue.length > 0 && (
            <span
              onClick={handleResetSelection}
              style={{
                color: '#E91E63',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Reset selection
            </span>
          )}
          {level && onClearLevel && (
            <IconButton
              size="small"
              onClick={handleClearLevel}
              sx={{
                padding: '2px',
                color: '#6B7280',
                '&:hover': {
                  color: '#EF4444',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Autocomplete Dropdown */}
      <FormControl fullWidth>
        <Autocomplete
          ref={autocompleteRef}
          multiple
          disableCloseOnSelect
          disableClearable
          disabled={disabled}
          options={keywordOptions}
          value={normalizedValue}
          onChange={(_event, newValue) => onChange(newValue)}
          open={open}
          onOpen={() => {
            justOpenedRef.current = true;
            requestAnimationFrame(() => { justOpenedRef.current = false; });
            setOpen(true);
            onActivate?.();
          }}
          onClose={(_event, reason) => {
            if (reason === 'toggleInput' && justOpenedRef.current) {
              justOpenedRef.current = false;
              return;
            }
            if (reason === 'blur') {
              const relatedTarget = (_event as React.FocusEvent).relatedTarget as HTMLElement | null;
              if (
                relatedTarget?.closest('.MuiAutocomplete-popper') ||
                (relatedTarget && autocompleteRef.current?.contains(relatedTarget))
              ) {
                return;
              }
            }
            setSearchText('');
            setOpen(false);
            onDeactivate?.();
          }}
          filterOptions={(options) => {
            if (!searchText.trim()) return options;
            const q = searchText.toLowerCase();
            return options.filter((opt) => opt.toLowerCase().includes(q));
          }}
          loading={isLoading}
          loadingText={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
              <CircularProgress size={16} />
              <span>Loading options...</span>
            </Box>
          }
          limitTags={0}
          getLimitTagsText={(more) => `${more} items selected`}
          ListboxComponent={VirtualizedListbox}
          popupIcon={<KeyboardArrowDownIcon sx={{ color: '#6B7280', fontSize: '20px' }} />}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                disabled
                  ? "Select a Brand or ASIN first"
                  : normalizedValue.length === 0
                    ? "Select Keywords"
                    : `${normalizedValue.length} items selected`
              }
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option}>
              <StyledCheckbox checked={selected} />
              <span style={{ marginLeft: '8px', fontSize: '14px' }}>{option}</span>
            </li>
          )}
          noOptionsText={isLoading ? "Loading options..." : "No options available"}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '8px',
              minHeight: '44px',
              fontSize: '14px',
              fontFamily: 'inherit',
              padding: '4px 14px !important',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #E5E7EB',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #068E75',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '2px solid #068E75',
              },
            },
            '& .MuiAutocomplete-input': {
              padding: '6px 0 !important',
            },
            ...(disabled && {
              opacity: 0.5,
              pointerEvents: 'none',
            }),
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                marginTop: '2px',
              },
            },
            listbox: {
              searchText,
              onSearchChange: setSearchText,
              searchPlaceholder: "Search keywords...",
              onSearchBlur: handleSearchBlur,
              sx: {
                padding: 0,
                overflow: 'hidden',
                '&::-webkit-scrollbar': { display: 'none' },
                '& .MuiAutocomplete-option': {
                  padding: '8px 12px',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                  '&[aria-selected="true"]': {
                    backgroundColor: 'transparent',
                  },
                  '&[aria-selected="true"]:hover': {
                    backgroundColor: '#F3F4F6',
                  },
                },
              },
            } as any,
          }}
        />
      </FormControl>

      {/* Selected Items as Chips below dropdown */}
      {normalizedValue.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            marginTop: '8px',
            maxWidth: '100%',
          }}
        >
          {normalizedValue.map((keyword) => (
            <StyledChip
              key={keyword}
              label={keyword}
              size="small"
              onDelete={handleChipDelete(keyword)}
              deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default KeywordFilter;
